import { ApiResult, HttpClient } from "../http.js";
import { AftalesedlDto, AftaleseddelStatus } from "../types/aftalesedler.js";

export interface CreateAftalesedlPayload {
  title: string;
  description?: string | null;
}

export interface UpdateAftalesedlPayload {
  title: string;
  description?: string | null;
}

export interface AftalesedlLineItemPayload {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
}

/**
 * Aftalesedler (project extra-work notes). The web feature speaks a simple
 * `description + amount` model; the amount is carried as a single line item
 * (`quantity 1`, `unitPrice = amount`) so the aggregate `totalExclVat` equals
 * the entered ex-VAT amount. Only the non-signing endpoints are wired here —
 * the customer-signing workflow (send/sign/decline) is intentionally omitted.
 */
export class AftalesedlerApi {
  constructor(private readonly http: HttpClient) {}

  listByProject(projectId: string): Promise<ApiResult<AftalesedlDto[]>> {
    return this.http.get<AftalesedlDto[]>(
      `/api/projects/${encodeURIComponent(projectId)}/aftalesedler`,
    );
  }

  create(projectId: string, payload: CreateAftalesedlPayload): Promise<ApiResult<AftalesedlDto>> {
    return this.http.post<AftalesedlDto>(
      `/api/projects/${encodeURIComponent(projectId)}/aftalesedler`,
      { body: payload },
    );
  }

  get(id: string): Promise<ApiResult<AftalesedlDto>> {
    return this.http.get<AftalesedlDto>(`/api/aftalesedler/${encodeURIComponent(id)}`);
  }

  update(id: string, payload: UpdateAftalesedlPayload): Promise<ApiResult<AftalesedlDto>> {
    return this.http.put<AftalesedlDto>(`/api/aftalesedler/${encodeURIComponent(id)}`, {
      body: payload,
    });
  }

  /**
   * PATCH /api/aftalesedler/{id}/status — approve (Godkendt), reject (Afvist) or re-open
   * (Fremlagt). Needs `aftalesedler.approve` (admin and projektleder by default).
   */
  setStatus(id: string, status: AftaleseddelStatus): Promise<ApiResult<AftalesedlDto>> {
    return this.http.patch<AftalesedlDto>(`/api/aftalesedler/${encodeURIComponent(id)}/status`, {
      body: { status },
    });
  }

  remove(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/aftalesedler/${encodeURIComponent(id)}`);
  }

  addLineItem(id: string, payload: AftalesedlLineItemPayload): Promise<ApiResult<AftalesedlDto>> {
    return this.http.post<AftalesedlDto>(
      `/api/aftalesedler/${encodeURIComponent(id)}/line-items`,
      { body: payload },
    );
  }

  updateLineItem(
    id: string,
    itemId: string,
    payload: AftalesedlLineItemPayload,
  ): Promise<ApiResult<AftalesedlDto>> {
    return this.http.put<AftalesedlDto>(
      `/api/aftalesedler/${encodeURIComponent(id)}/line-items/${encodeURIComponent(itemId)}`,
      { body: payload },
    );
  }

  removeLineItem(id: string, itemId: string): Promise<ApiResult<AftalesedlDto>> {
    return this.http.delete<AftalesedlDto>(
      `/api/aftalesedler/${encodeURIComponent(id)}/line-items/${encodeURIComponent(itemId)}`,
    );
  }
}
